import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Store
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isSuspended: false,
      suspendedMessage: '',
      isTrialExpired: false,
      trialExpiredMessage: '',
      isLoading: false,
      error: null,

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await axios.post(`${API}/auth/login`, { email, password });
          const { access_token, user } = response.data;
          set({ 
            user, 
            token: access_token, 
            isAuthenticated: true, 
            isLoading: false 
          });
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.detail || 'Giriş başarısız';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await axios.post(`${API}/auth/register`, data);
          const { access_token, user } = response.data;
          set({ 
            user, 
            token: access_token, 
            isAuthenticated: true, 
            isLoading: false 
          });
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.detail || 'Kayıt başarısız';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      logout: () => {
        set({ 
          user: null, 
          token: null, 
          isAuthenticated: false,
          isSuspended: false,
          suspendedMessage: '',
          isTrialExpired: false,
          trialExpiredMessage: '',
          error: null 
        });
      },

      clearError: () => set({ error: null }),

      setUser: (user) => set({ user }),

      setSuspended: (isSuspended, message) => set({ isSuspended, suspendedMessage: message || '' }),

      setTrialExpired: (isTrialExpired, message) => set({ isTrialExpired, trialExpiredMessage: message || '' }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        token: state.token, 
        isAuthenticated: state.isAuthenticated,
        isSuspended: state.isSuspended,
        suspendedMessage: state.suspendedMessage,
        isTrialExpired: state.isTrialExpired,
        trialExpiredMessage: state.trialExpiredMessage,
      }),
    }
  )
);

// Create axios instance with auth header
export const getAuthAxios = () => {
  const token = useAuthStore.getState().token;
  const instance = axios.create({
    baseURL: API,
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  });
  // Intercept 403 SUSPENDED responses
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail || '';
        if (detail.startsWith('SUSPENDED:')) {
          useAuthStore.getState().setSuspended(true, detail.replace('SUSPENDED:', ''));
        } else if (detail.startsWith('TRIAL_EXPIRED:')) {
          useAuthStore.getState().setTrialExpired(true, detail.replace('TRIAL_EXPIRED:', ''));
        }
      }
      return Promise.reject(error);
    }
  );
  return instance;
};

// Dashboard Store
export const useDashboardStore = create((set) => ({
  stats: null,
  monthlyCollections: [],
  personnelPerformance: [],
  whatsappStatus: null,
  collectionReport: null,
  paymentReport: null,
  isLoading: false,
  error: null,

  fetchStats: async () => {
    set({ isLoading: true });
    try {
      const api = getAuthAxios();
      const [statsRes, monthlyRes, performanceRes, whatsappRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/monthly-collections'),
        api.get('/dashboard/personnel-performance').catch(() => ({ data: [] })),
        api.get('/whatsapp/status').catch(() => ({ data: { status: 'disconnected' } })),
      ]);
      set({
        stats: statsRes.data,
        monthlyCollections: monthlyRes.data,
        personnelPerformance: performanceRes.data,
        whatsappStatus: whatsappRes.data,
        isLoading: false,
      });
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Veri yüklenemedi', isLoading: false });
    }
  },

  fetchCollectionReport: async (dateFrom, dateTo) => {
    try {
      const api = getAuthAxios();
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/dashboard/collection-report', { params });
      set({ collectionReport: res.data });
    } catch (error) {
      console.error('Collection report error:', error);
    }
  },

  fetchPaymentReport: async (dateFrom, dateTo) => {
    try {
      const api = getAuthAxios();
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/dashboard/payment-report', { params });
      set({ paymentReport: res.data });
    } catch (error) {
      console.error('Payment report error:', error);
    }
  },
}));

// Cari Store
export const useCariStore = create((set) => ({
  caris: [],
  selectedCari: null,
  isLoading: false,
  error: null,

  fetchCaris: async () => {
    set({ isLoading: true });
    try {
      const api = getAuthAxios();
      const response = await api.get('/cari');
      set({ caris: response.data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Cariler yüklenemedi', isLoading: false });
    }
  },

  createCari: async (data) => {
    try {
      const api = getAuthAxios();
      const response = await api.post('/cari', data);
      set((state) => ({ caris: [...state.caris, response.data] }));
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Cari oluşturulamadı' };
    }
  },

  updateCari: async (id, data) => {
    try {
      const api = getAuthAxios();
      const response = await api.put(`/cari/${id}`, data);
      set((state) => ({
        caris: state.caris.map((c) => (c.id === id ? response.data : c)),
      }));
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Cari güncellenemedi' };
    }
  },

  deleteCari: async (id) => {
    try {
      const api = getAuthAxios();
      await api.delete(`/cari/${id}`);
      set((state) => ({ caris: state.caris.filter((c) => c.id !== id) }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Cari silinemedi' };
    }
  },

  bulkDeleteCari: async (ids) => {
    try {
      const api = getAuthAxios();
      const res = await api.post('/cari/bulk-delete', { ids });
      set((state) => ({ caris: state.caris.filter((c) => !ids.includes(c.id)) }));
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Toplu silme başarısız' };
    }
  },

  bulkAssignCari: async (ids, assignedUserId) => {
    try {
      const api = getAuthAxios();
      const res = await api.post('/cari/bulk-assign', { ids, assigned_user_id: assignedUserId });
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Toplu atama başarısız' };
    }
  },

  setSelectedCari: (cari) => set({ selectedCari: cari }),
}));

// Payment Store
export const usePaymentStore = create((set) => ({
  payments: [],
  selectedPayment: null,
  isLoading: false,
  error: null,
  filter: 'all', // all, pending, paid, overdue

  fetchPayments: async (status = null) => {
    set({ isLoading: true });
    try {
      const api = getAuthAxios();
      const url = status ? `/payments?status=${status}` : '/payments';
      const response = await api.get(url);
      set({ payments: response.data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Ödemeler yüklenemedi', isLoading: false });
    }
  },

  createPayment: async (data) => {
    try {
      const api = getAuthAxios();
      const response = await api.post('/payments', data);
      set((state) => ({ payments: [...state.payments, response.data] }));
      return { success: true, data: response.data };
    } catch (error) {
      if (error.response?.status === 409) {
        return { success: false, conflict: true, existing_payment: error.response.data.existing_payment, error: error.response.data.detail };
      }
      return { success: false, error: error.response?.data?.detail || 'Ödeme sözü oluşturulamadı' };
    }
  },

  updatePayment: async (id, data) => {
    try {
      const api = getAuthAxios();
      const response = await api.put(`/payments/${id}`, data);
      set((state) => ({
        payments: state.payments.map((p) => (p.id === id ? response.data : p)),
      }));
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Ödeme sözü güncellenemedi' };
    }
  },

  markAsPaid: async (id) => {
    try {
      const api = getAuthAxios();
      const response = await api.post(`/payments/${id}/mark-paid`);
      set((state) => ({
        payments: state.payments.map((p) => (p.id === id ? response.data : p)),
      }));
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Ödeme durumu güncellenemedi' };
    }
  },

  deletePayment: async (id) => {
    try {
      const api = getAuthAxios();
      await api.delete(`/payments/${id}`);
      set((state) => ({ payments: state.payments.filter((p) => p.id !== id) }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Ödeme sözü silinemedi' };
    }
  },

  setFilter: (filter) => set({ filter }),
  setSelectedPayment: (payment) => set({ selectedPayment: payment }),
}));

// Personnel Store
export const usePersonnelStore = create((set) => ({
  personnel: [],
  isLoading: false,
  error: null,

  fetchPersonnel: async () => {
    set({ isLoading: true });
    try {
      const api = getAuthAxios();
      const response = await api.get('/personnel');
      set({ personnel: response.data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Personel yüklenemedi', isLoading: false });
    }
  },

  createPersonnel: async (data) => {
    try {
      const api = getAuthAxios();
      const response = await api.post('/personnel', data);
      set((state) => ({ personnel: [...state.personnel, response.data] }));
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Personel oluşturulamadı' };
    }
  },

  updatePersonnel: async (id, data) => {
    try {
      const api = getAuthAxios();
      const response = await api.put(`/personnel/${id}`, data);
      set((state) => ({
        personnel: state.personnel.map((p) => (p.id === id ? response.data : p)),
      }));
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Personel güncellenemedi' };
    }
  },

  deletePersonnel: async (id) => {
    try {
      const api = getAuthAxios();
      await api.delete(`/personnel/${id}`);
      set((state) => ({ personnel: state.personnel.filter((p) => p.id !== id) }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Personel silinemedi' };
    }
  },
}));

// Company Store
export const useCompanyStore = create((set) => ({
  company: null,
  isLoading: false,
  error: null,

  fetchCompany: async () => {
    set({ isLoading: true });
    try {
      const api = getAuthAxios();
      const response = await api.get('/companies/me');
      set({ company: response.data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Şirket bilgisi yüklenemedi', isLoading: false });
    }
  },

  updateCompany: async (id, data) => {
    try {
      const api = getAuthAxios();
      const response = await api.put(`/companies/${id}`, data);
      set({ company: response.data });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Şirket güncellenemedi' };
    }
  },

  uploadLogo: async (id, file) => {
    try {
      const api = getAuthAxios();
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/companies/${id}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      set((state) => ({
        company: state.company ? { ...state.company, logo: response.data.logo } : null,
      }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Logo yüklenemedi' };
    }
  },

  renewSubscription: async (id, data) => {
    try {
      const api = getAuthAxios();
      const response = await api.post(`/companies/${id}/renew-subscription`, data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Yenileme başarısız' };
    }
  },
}));
